import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import OutsideClickHandler from 'react-outside-click-handler';
import { useTranslation } from 'react-i18next';

import Icon from '../Icon';
import Tooltip from '../Tooltip';
import ListMenu from '../ListMenu';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@ohif/ui-next';
import { RiDeleteBin5Line, RiEditBoxFill } from 'react-icons/ri';
import { FaEdit, FaTrashAlt } from 'react-icons/fa';

const baseClasses = {
  Button: 'flex items-center rounded-md border-transparent group/button',
  Primary: 'h-[28px] rounded-tl-md rounded-bl-md group/primary',
  Secondary:
    'h-[28px] flex items-center justify-center rounded-tr-md rounded-br-md w-[16px] border-2 border-transparent group/secondary',
  SecondaryIcon: 'w-4 h-full stroke-1',
  Separator: 'border-l py-3',
  Content: '',
};

const classes = {
  Button: ({ isExpanded }) => classNames(baseClasses.Button),
  Interface: 'h-[30px] flex flex-row items-center',
  Primary: ({ isExpanded, isActive }) =>
    classNames(
      baseClasses.Primary,
      isActive
        ? isExpanded
          ? 'border-primary-dark !bg-primary-dark hover:border-primary-dark !text-primary-light'
          : 'border-primary-light bg-primary-light rounded-md'
        : `focus:!text-black focus:!rounded-md focus:!border-primary-light focus:!bg-primary-light ${isExpanded ? 'border-primary-dark bg-primary-dark !text-primary-light' : 'border-secondary-dark group-hover/button:border-primary-dark group-hover/button:text-primary-light hover:!bg-primary-dark hover:border-primary-dark focus:!text-black'}`
    ),
  Secondary: ({ isExpanded, primary }) =>
    classNames(
      baseClasses.Secondary,
      isExpanded
        ? 'bg-primary-light !rounded-tr-md !rounded-br-md'
        : primary.isActive
          ? ''
          : 'hover:bg-primary-dark'
    ),
  SecondaryIcon: ({ isExpanded }) =>
    classNames(
      baseClasses.SecondaryIcon,
      isExpanded
        ? 'text-primary-dark'
        : 'text-primary-active group-hover/secondary:text-primary-light'
    ),
  Separator: ({ primary, isExpanded, isHovering }) =>
    classNames(
      baseClasses.Separator,
      isHovering || isExpanded || primary.isActive ? 'border-transparent' : 'border-primary-active'
    ),
  Content: ({ isExpanded }) => classNames(baseClasses.Content, isExpanded ? 'block' : 'hidden'),
};

const DefaultListItemRenderer = props => {
  const { t, icon, label, className, isActive } = props;
  return (
    <div
      className={classNames(
        'flex h-8 w-full select-none flex-row items-center p-3',
        'whitespace-pre text-base',
        className,
        `${isActive ? 'hover:opacity-80' : 'hover:bg-primary-dark'}`
      )}
    >
      {icon && (
        <span className="mr-3">
          <Icon
            name={icon}
            className="h-[22px] w-[22px]"
          />
        </span>
      )}
      <span className="mr-3">{t?.(label)}</span>
    </div>
  );
};

/**
 * This is a more generic version of SplitButton without the isActive
 * and other interaction props
 */
const SplitButton = ({
  groupId,
  userInfo,
  primary,
  secondary,
  items,
  renderer = null,
  onInteraction,
  Component = Icon,
}) => {
  const { t } = useTranslation('Buttons');
  const [state, setState] = useState({ isHovering: false, isExpanded: false });

  const toggleExpanded = () =>
    setState(prevState => ({ ...prevState, isExpanded: !prevState.isExpanded }));

  const setHover = hovering => setState({ ...state, isHovering: hovering });
  const collapse = () => setState({ ...state, isExpanded: false });

  const listItemRenderer = renderer || DefaultListItemRenderer;
  const primaryClassNames = classNames(
    classes.Primary({
      isExpanded: state.isExpanded,
      isActive: primary.isActive,
    }),
    primary.className
  );

  const [selectedIndex, setSelectedIndex] = useState(null);
  const flex = 'flex flex-row justify-between items-center';
  const theme = 'bg-indigo-dark text-white';
  const onClickHandler = ({ item, index, isSelected }) => {
    setSelectedIndex(index);
    onClick({ item, selectedIndex: index });
    item.onClick?.({ ...item, index, isSelected });
  };
  // const ListItem = ({ item, index, isSelected }) => {

  //   return (
  //     <DropdownMenuLabel
  //       className={classNames(flex, theme, {
  //         'cursor-pointer': !item.disabled,
  //         'ohif-disabled': item.disabled,
  //       })}
  //       onClick={onClickHandler}
  //       data-cy={item.id}
  //     >
  //       {listItemRenderer({ ...item, index, isSelected })}
  //     </DropdownMenuLabel>
  //   );
  // };

  // console.warn('items ', items);

  return (
    <OutsideClickHandler
      onOutsideClick={collapse}
      disabled={!state.isExpanded}
    >
      <div id="SplitButton">
        <DropdownMenu open={state.isExpanded}>
          <div
            className={classes.Button({ ...state })}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <div className={classes.Interface}>
              <div onClick={collapse}>
                <Component
                  key={primary.id}
                  {...primary}
                  onInteraction={onInteraction}
                  rounded="none"
                  className={primaryClassNames}
                  data-tool={primary.id}
                  data-cy={`${groupId}-split-button-primary`}
                />
              </div>
              <div className={classes.Separator({ ...state, primary })}></div>
              <DropdownMenuTrigger asChild>
                <div
                  className={classes.Secondary({ ...state, primary })}
                  onClick={() => {
                    // Hentikan bubbling
                    toggleExpanded();
                  }}
                  data-cy={`${groupId}-split-button-secondary`}
                >
                  <Tooltip
                    isDisabled={state.isExpanded || !secondary.tooltip}
                    content={secondary.tooltip}
                    className="h-full"
                  >
                    <Icon
                      name={secondary.icon}
                      className={classes.SecondaryIcon({ ...state })}
                    />
                  </Tooltip>
                </div>
              </DropdownMenuTrigger>
            </div>
          </div>
          <DropdownMenuContent className="p-0">
            <div
              className={classes.Content({ ...state })}
              data-cy={`${groupId}-list-menu`}
            >
              <div
                className="flex flex-col gap-[4px] overflow-auto rounded-md p-1"
                style={{ maxHeight: 'calc(100vh - 5rem)' }}
              >
                {items.map((item, index) => {
                  const isSelected = selectedIndex === index;
                  return (
                    //   <ListItem
                    //     key={`ListItem${index}`}
                    //     index={index}
                    //     isSelected={selectedIndex === index}
                    //     item={item}
                    //   />
                    <DropdownMenuItem
                      key={`ListItem${index}`}
                      className={classNames(flex, theme, {
                        'cursor-pointer': !item.disabled,
                        'ohif-disabled': item.disabled,
                      })}
                      onClick={e => {
                        e.stopPropagation();
                        setSelectedIndex(index);
                        item.onClick?.({ ...item, index, isSelected });
                        // collapse();
                      }}
                      data-cy={item.id}
                    >
                      {listItemRenderer({ ...item, index, isSelected, t })}
                      <></>
                      {groupId === 'WindowLevel' &&
                        item.createdby &&
                        item.createdby === userInfo.UserName && (
                          <div className="flex gap-2 bg-transparent px-2">
                            <Button
                              onClick={e => {
                                e.stopPropagation();
                                item.onEditClick?.({ ...item, index, isSelected });
                              }}
                              title="Edit Preset Window Level"
                              className="bg-transparent px-0 hover:bg-transparent"
                            >
                              <FaEdit size={15} />
                            </Button>
                            <Button
                              onClick={e => {
                                e.stopPropagation();
                                item.onDeleteClick?.({ ...item, index, isSelected });
                              }}
                              title="Delete Preset Window Level"
                              className="bg-transparent px-0 hover:bg-transparent"
                            >
                              <FaTrashAlt />
                            </Button>
                          </div>
                        )}
                    </DropdownMenuItem>
                  );
                })}
              </div>

              {/* <ListMenu
                items={items}
                onClick={collapse}
                renderer={args => listItemRenderer({ ...args, t })}
              /> */}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </OutsideClickHandler>
  );
};

SplitButton.propTypes = {
  groupId: PropTypes.string.isRequired,
  userInfo: PropTypes.any,
  primary: PropTypes.object.isRequired,
  secondary: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired,
  renderer: PropTypes.func,
  isActive: PropTypes.bool,
  onInteraction: PropTypes.func.isRequired,
  Component: PropTypes.elementType,
  interactionType: PropTypes.oneOf(['action', 'tool', 'toggle']),
};

export default SplitButton;
